import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import type { MediaImageProps } from '@elfsight/widget-media';
import { MediaImage } from '@elfsight/widget-media';
import type { PlayerAPI } from '@elfsight/widget-media-video';
import type { VideoMediaValue } from '../../../../data/sources';
import { useLayout } from '../../../../layout';
import { usePopup } from '../../../../popup';
import { useSocialFeedSettings } from '../../../../utils';
import { usePost } from '../../../Post';
import { IframeVideoPlayer } from './IframeVideoPlayer';
import { useVideoIntersection } from './useVideoIntersection';
import { VideoDummy } from './VideoDummy';

const LazyMediaVideo = lazy(async () => ({
  default: (await import('@elfsight/widget-media-video')).MediaVideo
}));

const YOUTUBE_CONTROLS_DISPLAY_DELAY = 3500;

const POPUP_SUFFIX = '-popup';

export type PickedMediaImageProps = Pick<
  MediaImageProps,
  | 'className'
  | 'alt'
  | 'ratio'
  | 'onLoad'
  | 'onError'
  | 'mode'
  | 'loaderColor'
  | 'borderRadius'
  | 'backgroundColor'
>;

interface MediaVideoProps {
  media: VideoMediaValue;
  mediaImageProps: PickedMediaImageProps;
  className?: string;
}

function _MediaVideo({ media, mediaImageProps, className }: MediaVideoProps) {
  const { postVideoAutoplay, openLinksNewTab } = useSocialFeedSettings();
  const { index: popupIndex } = usePopup();
  const {
    id,
    index,
    isInPopup,
    isHovered,
    source: { type: platform, url: sourceURL }
  } = usePost();
  const { rerender } = useLayout();

  const videoRef = useRef<PlayerAPI>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>(
    'loading'
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(isInPopup);
  const [loadedSeconds, setLoadedSeconds] = useState(0);

  const hasIframeVideoPlayer = platform === 'vkontakte';
  const isYoutube = platform === 'youtube';
  const isTikTok = platform === 'tiktok';
  const isTwitter = platform === 'twitter';
  const canUseAutoplay = postVideoAutoplay && !hasIframeVideoPlayer;

  // Используем разные ID для видео в лейауте и в попапе,
  // чтобы они не конфликтовали при регистрации
  const videoId = isInPopup ? `${id}${POPUP_SUFFIX}` : id;

  const { videoIntersectionRef, hasBeenInView } = useVideoIntersection({
    videoId,
    index,
    isInPopup,
    videoRef,
    canUseAutoplay,
    isHovered
  });

  const redirectToVideoSource = useCallback(
    () => window.open(sourceURL, openLinksNewTab ? '_blank' : '_top'),
    [sourceURL, openLinksNewTab]
  );

  const thumbnailMode: 'preview' | 'dummy' | 'none' = (() => {
    if (isInPopup) {
      // TikTok может не возвращать ссылку на видео, когда используется музыка с авторскими правами.
      // Twitter (X) может не возвращать ссылку на видео, если оно, например, помечено как 18+.
      // В этом случае отображаем заглушку, которая при клике будет вести на страницу видео в соцсети
      return (isTikTok || isTwitter) && !loadedSeconds ? 'dummy' : 'none';
    }

    // Отображаем preview, когда:
    // Пока видео не начало воспроизводиться хотя бы один раз. Это предотвращает показ лоадера плеера перед началом воспроизведения
    // До первого появления в области видимости.
    // Пока не загружено достаточно данных для старта видео
    if (!hasStartedPlaying || !hasBeenInView || !loadedSeconds) {
      return 'preview';
    }

    // Отображаем preview для YouTube пока не началось воспроизведение
    if (isYoutube && canUseAutoplay && !isPlaying) {
      return 'preview';
    }

    return 'none';
  })();

  const onReadyHandler = useCallback(() => {
    if (status !== 'loading' || !postVideoAutoplay) {
      return;
    }

    setStatus('loaded');

    requestAnimationFrame(() => {
      rerender?.();
    });

    if (isInPopup && videoRef.current && popupIndex === index) {
      videoRef.current.play();
    }
  }, [rerender, status, isInPopup, popupIndex, index, postVideoAutoplay]);

  const onErrorHandler = useCallback(() => {
    setStatus('failed');
  }, []);

  const onPlayHandler = useCallback(() => {
    setIsPlaying(true);

    setTimeout(
      () => {
        setHasStartedPlaying(true);
      },
      isYoutube ? YOUTUBE_CONTROLS_DISPLAY_DELAY : 0
    );
  }, [isYoutube]);

  const onPauseHandler = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const onProgress = useCallback(
    (progress: { playedSeconds: number; loadedSeconds: number }) => {
      setLoadedSeconds(progress.loadedSeconds);
    },
    []
  );

  useEffect(() => {
    setLoadedSeconds(0);
  }, [media.src]);

  if (hasIframeVideoPlayer) {
    return (
      <IframeVideoPlayer
        src={media.src}
        type={media.videoFormat}
        title={media.title}
        className={className}
      />
    );
  }

  return (
    <Suspense fallback={null}>
      <IntersectionContainer ref={videoIntersectionRef} className={className}>
        {hasBeenInView && (
          <Video
            muted
            onPlay={onPlayHandler}
            onPause={onPauseHandler}
            onReady={onReadyHandler}
            onProgress={onProgress}
            loop={canUseAutoplay && !isInPopup}
            ref={videoRef}
            shouldFill={!isInPopup && canUseAutoplay && !isYoutube}
            controls={isInPopup}
            src={media.src}
            ratio={mediaImageProps.ratio || 0}
            backgroundColor={mediaImageProps.backgroundColor}
            playerProps={{ onError: onErrorHandler }}
            _showThumbnail={thumbnailMode !== 'none'}
            _disablePointerEvents={isYoutube && !isInPopup}
          />
        )}

        {thumbnailMode === 'preview' && (
          <MediaImage src={media.thumbnail} {...mediaImageProps} />
        )}

        {thumbnailMode === 'dummy' && (
          <VideoDummy
            src={media.thumbnail}
            mediaImageProps={mediaImageProps}
            mode={status === 'failed' ? 'redirect' : 'loading'}
            onClick={status === 'failed' ? redirectToVideoSource : undefined}
          />
        )}
      </IntersectionContainer>
    </Suspense>
  );
}

export const MediaVideo = styled(_MediaVideo)``;

const IntersectionContainer = styled.div`
  width: 100%;
`;

const Video = styled(LazyMediaVideo)<{
  _showThumbnail: boolean;
  _disablePointerEvents: boolean;
}>`
  cursor: pointer;

  ${({ _showThumbnail }) =>
    _showThumbnail &&
    css`
      position: absolute;
      visibility: hidden;
      pointer-events: none;
    `};

  ${({ _disablePointerEvents }) =>
    _disablePointerEvents && 'pointer-events: none;'};
`;
